import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { insertItem, patch, updateItem } from '@ngxs/store/operators';
import { sumBy } from 'lodash';
import { DebtScheduleItem } from 'ng2-app/models/dscr/DebtScheduleItem.struct';
import { DebtScheduleService } from 'ng2-app/services/debt-schedule/debt-schedule.service';
import { CreateDebtScheduleItem, GetDebtSchedules, UpdateDebtScheduleItem, DeleteDebtScheduleItem } from 'ng2-app/store/debt-schedule/debt-schedule.actions';
import { catchError, tap } from 'rxjs/operators';

export interface DebtScheduleStateModel {
  debtScheduleItems: DebtScheduleItem[];
  loading: boolean;
  debtScheduleItem: DebtScheduleItem;
}

@State({
  name: 'DebtSchedule',
})
@Injectable({
  providedIn: 'root',
})
export class DebtScheduleState {
  @Selector()
  static debtScheduleItems(state: DebtScheduleStateModel) {
    const debtTypes = DebtScheduleService.debtTypeOptions;
    return state.debtScheduleItems.map((debtScheduleItem: DebtScheduleItem) => {
      const debtType = debtTypes.find( dt => dt.value === debtScheduleItem.debtType );
      return {
        ...debtScheduleItem,
        debtTypeLabel: debtType?.label,
      };
    });
  }

  @Selector()
  static debtScheduleItem(state: DebtScheduleStateModel) {
    return (id: number) => state.debtScheduleItems.find(item => item.id == id);
  }

  @Selector()
  static totalPayments(state: DebtScheduleStateModel) {
    return sumBy(state.debtScheduleItems, item => parseFloat(item.paymentAmount as unknown as string) ) || 0;
  }

  @Selector()
  static totalBalance(state: DebtScheduleStateModel) {
    return sumBy(state.debtScheduleItems, item => parseFloat(item.currentBalance as unknown as string) ) || 0;
  }

  constructor(
    private debtScheduleService: DebtScheduleService,
  ) {}

  @Action(GetDebtSchedules)
  GetDebtSchedules(
    ctx: StateContext<DebtScheduleStateModel>,
    { borrowerId }: GetDebtSchedules,
  ) {
    ctx.patchState({loading: true});
    return this.debtScheduleService.getDebtSchedules(borrowerId)
      .pipe(
        catchError(err => {
          ctx.patchState({
            loading: false,
          });
          throw err;
        }),
        tap( debtScheduleItems => {
          ctx.patchState({
            debtScheduleItems,
            loading: false,
          });
        })
      );
  }

  @Action(CreateDebtScheduleItem)
  CreateDebtScheduleItem(
    ctx: StateContext<DebtScheduleStateModel>,
    { borrowerId, debtScheduleItem }: CreateDebtScheduleItem,
  ) {
    ctx.patchState({loading: true});
    return this.debtScheduleService.createDebtScheduleItem(borrowerId, debtScheduleItem)
      .pipe(
        catchError(err => {
          ctx.patchState({
            loading: false,
          });
          throw err;
        }),
        tap( debtScheduleItems => {
          ctx.setState(patch({
            debtScheduleItems: insertItem(debtScheduleItems),
            loading: false,
          }));
        })
      );
  }

  @Action(UpdateDebtScheduleItem)
  UpdateDebtScheduleItem(
    ctx: StateContext<DebtScheduleStateModel>,
    { borrowerId, debtScheduleItem }: UpdateDebtScheduleItem,
  ) {
    ctx.patchState({loading: true});
    return this.debtScheduleService.updateDebtScheduleItem(borrowerId, debtScheduleItem)
      .pipe(
        catchError(err => {
          ctx.patchState({
            loading: false,
          });
          throw err;
        }),
        tap( updatedItem => {
          ctx.setState(patch({
            debtScheduleItems: updateItem(
              existingItem => existingItem.id === updatedItem.id,
              patch({
              ...updatedItem,
            })),
            loading: false,
          }));
        })
      );
  }

  @Action(DeleteDebtScheduleItem)
  DeleteDebtScheduleItem(
    ctx: StateContext<DebtScheduleStateModel>,
    { id }: DeleteDebtScheduleItem,
  ) {
    ctx.patchState({loading: true});
    return this.debtScheduleService.deleteDebtScheduleItem(id)
      .pipe(
        catchError(err => {
          ctx.patchState({
            loading: false,
          });
          throw err;
        }),
        tap( () => {
          ctx.setState(patch({
            debtScheduleItems: ctx.getState().debtScheduleItems.filter( i => i.id !== id ),
            loading: false,
          }));
        })
      );
  }

}
